/**
 * Collection Pagination Handler
 * Implements hybrid Load More + SEO pagination with history.pushState()
 * 
 * Features:
 * - Load More button with AJAX loading
 * - URL updates via history.pushState() for back button support
 * - Device-specific thresholds
 * - Progress bar updates
 * - Product count updates
 * - Graceful degradation (works without JS via pagination links)
 * - Integrates with existing jQuery-based load-more.js
 */

class CollectionPagination {
  constructor() {
    this.container = document.querySelector('[data-pagination-container]');
    if (!this.container) {
      console.warn('CollectionPagination: Container not found');
      return;
    }

    // Configuration
    this.config = {
      mobile: {
        productsPerPage: 16,
        maxBeforePagination: 48,
        lazyLoadEnabled: false
      },
      desktop: {
        productsPerPage: 24,
        maxBeforePagination: 72,
        lazyLoadEnabled: true
      }
    };

    // Elements - support both new structure and existing structure (initialize first)
    this.elements = {
      productsGrid: document.querySelector('[data-products-grid]') || document.querySelector('#product-grid') || document.querySelector('.mrk-product-list'),
      loadMoreBtn: this.container.querySelector('[data-load-more-btn]'),
      loadMoreText: this.container.querySelector('[data-load-more-text]'),
      productCount: this.container.querySelector('[data-products-shown]'),
      progressFill: this.container.querySelector('[data-progress-fill]'),
      progressText: this.container.querySelector('[data-progress-text]'),
      paginationNav: this.container.querySelector('[data-pagination-nav]')
    };

    // State (initialize after elements so countCurrentProducts can use this.elements)
    const currentPage = parseInt(this.container.dataset.currentPage) || 1;
    const totalProducts = parseInt(this.container.dataset.totalProducts) || 0;
    const productsPerPage = parseInt(this.container.dataset.productsPerPage) || 20;
    
    this.state = {
      isLoading: false,
      currentPage: currentPage,
      totalPages: parseInt(this.container.dataset.totalPages) || 1,
      totalProducts: totalProducts,
      // Calculate products shown correctly: current page × products per page, capped at total
      productsLoaded: Math.min(currentPage * productsPerPage, totalProducts),
      collectionHandle: this.container.dataset.collectionHandle || ''
    };

    this.init();
  }

  init() {
    this.bindEvents();
    this.handlePopState();
    this.updateDeviceConfig();
    
    // Initialize UI with correct values
    this.updateProductCount();
    this.updateProgressBar();
    
    // Initialize canonical tag
    this.updateCanonicalTag(this.state.currentPage);
    
    // Handle resize for device-specific behavior
    window.addEventListener('resize', this.debounce(() => {
      this.updateDeviceConfig();
    }, 250));
  }

  bindEvents() {
    // Load More button click - use event delegation since button might be replaced
    if (this.container) {
      this.container.addEventListener('click', (e) => {
        // Handle Load More button
        const loadMoreBtn = e.target.closest('[data-load-more-btn]');
        if (loadMoreBtn && !loadMoreBtn.disabled) {
          e.preventDefault();
          this.loadMoreProducts();
          return;
        }

        // Handle pagination link clicks (for AJAX loading instead of full page reload)
        const link = e.target.closest('a.collection-pagination__link');
        if (link && !link.classList.contains('collection-pagination__link--disabled')) {
          e.preventDefault();
          e.stopPropagation();
          
          try {
            const href = link.getAttribute('href');
            if (!href) return;
            
            const url = new URL(href, window.location.origin);
            let page = url.searchParams.get('page');
            
            // If no page parameter, check if it's in the path or default to 1
            if (!page || page === '') {
              // Check if URL ends with ?page= or has page in path
              const pageMatch = href.match(/[?&]page=(\d+)/);
              if (pageMatch) {
                page = pageMatch[1];
              } else {
                page = '1';
              }
            }
            
            const pageNum = parseInt(page, 10);
            if (isNaN(pageNum) || pageNum < 1) {
              console.warn('Invalid page number:', page);
              return;
            }
            
            this.goToPage(pageNum);
          } catch (error) {
            console.error('Error parsing pagination link:', error);
            // Fallback: allow default navigation
            window.location.href = link.href;
          }
          return;
        }
      });
    }
  }

  handlePopState() {
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.page) {
        this.goToPage(e.state.page, false);
      } else {
        // Handle back to page 1
        const url = new URL(window.location.href);
        const page = url.searchParams.get('page') || 1;
        this.goToPage(parseInt(page), false);
      }
    });
  }

  updateDeviceConfig() {
    this.isMobile = window.innerWidth < 768;
    this.currentConfig = this.isMobile ? this.config.mobile : this.config.desktop;
  }

  countCurrentProducts() {
    // Safely check if productsGrid exists
    if (!this.elements || !this.elements.productsGrid) {
      // Try to find the grid directly if elements not initialized yet
      const productsGrid = document.querySelector('[data-products-grid]') || 
                          document.querySelector('#product-grid') || 
                          document.querySelector('.mrk-product-list');
      if (!productsGrid) return 0;
      
      // Use the found grid for counting
      const selectors = [
        '[data-product-card]',
        '.product-card',
        '.grid__item'
      ];
      
      for (const selector of selectors) {
        const products = productsGrid.querySelectorAll(selector);
        if (products.length > 0) {
          return products.length;
        }
      }
      
      // Fallback: count direct children
      return productsGrid.children.length;
    }
    
    // Support multiple product card selectors
    const selectors = [
      '[data-product-card]',
      '.product-card',
      '.grid__item'
    ];
    
    for (const selector of selectors) {
      const products = this.elements.productsGrid.querySelectorAll(selector);
      if (products.length > 0) {
        return products.length;
      }
    }
    
    // Fallback: count direct children (excluding text nodes and comments)
    let directChildren = 0;
    for (let i = 0; i < this.elements.productsGrid.children.length; i++) {
      const child = this.elements.productsGrid.children[i];
      // Count only element nodes (not text nodes or comments)
      if (child.nodeType === 1) {
        directChildren++;
      }
    }
    
    return directChildren || this.elements.productsGrid.children.length;
  }

  async loadMoreProducts() {
    if (this.state.isLoading) return;

    const nextPage = this.state.currentPage + 1;
    if (nextPage > this.state.totalPages) return;

    // Build next URL from current location so sort_by (and other query params) are preserved
    const nextUrl = this.buildPageUrl(nextPage);

    this.setLoadingState(true);
    // CLS: hold the incoming products' space now, inside the click's input window
    const placeholders = this.addLoadMorePlaceholders();

    try {
      const response = await fetch(nextUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get new products - support multiple grid selectors
      const gridSelectors = [
        '[data-products-grid]',
        '#product-grid',
        '.mrk-product-list'
      ];
      
      let newProducts = null;
      for (const selector of gridSelectors) {
        const grid = doc.querySelector(selector);
        if (grid) {
          newProducts = Array.from(grid.children);
          break;
        }
      }
      
      if (!newProducts || newProducts.length === 0) {
        // Try to find products in the response HTML directly
        const tempDiv = doc.createElement('div');
        tempDiv.innerHTML = html;
        const allProducts = tempDiv.querySelectorAll('.product-card, [data-product-card], .grid__item');
        if (allProducts.length > 0) {
          newProducts = Array.from(allProducts);
        }
      }
      
      if (newProducts && newProducts.length > 0) {
        // Append new products with animation class
        newProducts.forEach((product, index) => {
          const clone = product.cloneNode(true);
          clone.classList.add('is-new');
          if (!clone.hasAttribute('data-product-card')) {
            clone.setAttribute('data-product-card', '');
          }
          // Take a placeholder's slot where one is left, so nothing below the grid moves
          const slot = placeholders.shift();
          if (slot) {
            slot.replaceWith(clone);
          } else {
            this.elements.productsGrid.appendChild(clone);
          }
          
          // Remove animation class after animation completes
          setTimeout(() => {
            clone.classList.remove('is-new');
          }, 500 + (index * 50));
        });

        // Get pagination data from new page
        const newPagination = doc.querySelector('[data-pagination-container]');
        if (newPagination) {
          // Update container data attributes
          const nextPage = this.state.currentPage + 1;
          this.container.dataset.currentPage = nextPage;
          this.container.dataset.totalPages = newPagination.dataset.totalPages || this.state.totalPages;
          this.container.dataset.totalProducts = newPagination.dataset.totalProducts || this.state.totalProducts;
          this.container.dataset.productsPerPage = newPagination.dataset.productsPerPage || this.container.dataset.productsPerPage;
        }

        // Update state
        this.state.currentPage++;
        this.state.totalPages = parseInt(this.container.dataset.totalPages) || this.state.totalPages;
        this.state.totalProducts = parseInt(this.container.dataset.totalProducts) || this.state.totalProducts;
        const productsPerPage = parseInt(this.container.dataset.productsPerPage) || 20;
        
        // Calculate products shown correctly (current page × products per page, capped at total)
        this.state.productsLoaded = Math.min(this.state.currentPage * productsPerPage, this.state.totalProducts);

        // Update URL via history.pushState()
        this.updateUrl(this.state.currentPage);

        // Update UI
        this.updateProductCount();
        this.updateProgressBar();
        this.updateLoadMoreButton(doc);
        this.updatePaginationNav(doc);
        
        // Reinitialize swatches if function exists (for existing functionality)
        if (typeof initSwatches === 'function') {
          setTimeout(() => {
            initSwatches();
          }, 100);
        }
        
        // Reinitialize VAT toggle if needed
        if (typeof jQuery !== 'undefined' && jQuery.fn) {
          setTimeout(() => {
            this.reinitializeVATToggle();
          }, 100);
        }

        // Render "What's included" drawer descriptions for newly loaded bundle cards (Lexical JSON)
        if (typeof window.__processBundleDrawerDescriptions === 'function') {
          window.__processBundleDrawerDescriptions();
        }
      } else {
        // No more products
        this.hideLoadMoreButton();
      }

    } catch (error) {
      console.error('Error loading more products:', error);
      this.showError('Failed to load more products. Please try again.');
    } finally {
      // Unused placeholders (short last page, error) go
      placeholders.forEach((el) => el.remove());
      this.setLoadingState(false);
    }
  }

  /**
   * CLS (2026-10-10, INS-TECH-06). Load More used to append the next page only when the fetch returned,
   * more than 500ms after the tap, so the button, the pagination block and every section below it jumped
   * down by the height of the new rows (lab: 0.63 CLS on one tap, mobile). Placeholders sized like the
   * current last card fill the grid straight away, inside the tap's input window, which CLS excludes.
   * The products then take the placeholders' slots one for one.
   * @returns {HTMLElement[]} placeholders in grid order
   */
  addLoadMorePlaceholders() {
    const grid = this.elements.productsGrid;
    if (!grid || !grid.lastElementChild) return [];
    const perPage = parseInt(this.container.dataset.productsPerPage, 10) || 0;
    const loaded = this.countCurrentProducts();
    const remaining = this.state.totalProducts > 0 ? this.state.totalProducts - loaded : perPage;
    const count = Math.max(0, Math.min(perPage, remaining));
    // Grid rows stretch to their tallest card, so the tallest of the last few cards is the row height
    let height = 0;
    Array.from(grid.children).slice(-4).forEach((el) => { height = Math.max(height, el.offsetHeight); });
    if (!count || !height) return [];
    const placeholders = [];
    for (let i = 0; i < count; i++) {
      const el = document.createElement('div');
      el.className = 'collection-pagination__placeholder';
      el.setAttribute('aria-hidden', 'true');
      el.style.minHeight = height + 'px';
      grid.appendChild(el);
      placeholders.push(el);
    }
    return placeholders;
  }

  async goToPage(page, updateHistory = true) {
    if (this.state.isLoading || page === this.state.currentPage) return;

    this.setLoadingState(true);

    const url = this.buildPageUrl(page);

    try {
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Replace products grid - support multiple selectors
      const gridSelectors = [
        '[data-products-grid]',
        '#product-grid',
        '.mrk-product-list'
      ];
      
      let newGrid = null;
      for (const selector of gridSelectors) {
        const found = doc.querySelector(selector);
        if (found) {
          newGrid = found;
          break;
        }
      }
      
      if (newGrid && this.elements.productsGrid) {
        this.elements.productsGrid.innerHTML = newGrid.innerHTML;
        
        // Add animation to all products
        const productSelectors = ['[data-product-card]', '.product-card', '.grid__item'];
        let products = [];
        
        for (const selector of productSelectors) {
          products = Array.from(this.elements.productsGrid.querySelectorAll(selector));
          if (products.length > 0) break;
        }
        
        // Fallback: use direct children if no products found with selectors
        if (products.length === 0) {
          products = Array.from(this.elements.productsGrid.children).filter(child => child.nodeType === 1);
        }
        
        products.forEach((product, index) => {
          product.classList.add('is-new');
          setTimeout(() => {
            product.classList.remove('is-new');
          }, 500 + (index * 50));
        });
      }

      // Get pagination data from new page
      const newPagination = doc.querySelector('[data-pagination-container]');
      if (newPagination) {
        // Update container data attributes
        this.container.dataset.currentPage = page;
        this.container.dataset.totalPages = newPagination.dataset.totalPages || this.state.totalPages;
        this.container.dataset.totalProducts = newPagination.dataset.totalProducts || this.state.totalProducts;
        this.container.dataset.productsPerPage = newPagination.dataset.productsPerPage || this.state.totalProducts;
      }

      // Update state
      this.state.currentPage = page;
      this.state.totalPages = parseInt(this.container.dataset.totalPages) || this.state.totalPages;
      this.state.totalProducts = parseInt(this.container.dataset.totalProducts) || this.state.totalProducts;
      const productsPerPage = parseInt(this.container.dataset.productsPerPage) || 20;
      
      // Calculate products shown correctly
      this.state.productsLoaded = Math.min(page * productsPerPage, this.state.totalProducts);

      // Update URL
      if (updateHistory) {
        this.updateUrl(page);
      }

      // Update UI
      this.updateProductCount();
      this.updateProgressBar();
      this.updateLoadMoreButton(doc);
      this.updatePaginationNav(doc);
      
      // Reinitialize swatches
      if (typeof initSwatches === 'function') {
        setTimeout(() => {
          initSwatches();
        }, 100);
      }
      
      // Reinitialize VAT toggle
      if (typeof jQuery !== 'undefined' && jQuery.fn) {
        setTimeout(() => {
          this.reinitializeVATToggle();
        }, 100);
      }

      // Render "What's included" drawer descriptions for bundle cards (Lexical JSON) on new page
      if (typeof window.__processBundleDrawerDescriptions === 'function') {
        window.__processBundleDrawerDescriptions();
      }

      // Scroll to top of products
      const productsContainer = document.querySelector('[data-collection-products]') || 
                                document.querySelector('#CollectionProductGrid') ||
                                document.querySelector('#product-grid');
      if (productsContainer) {
        productsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

    } catch (error) {
      console.error('Error navigating to page:', error);
      this.showError('Failed to load page. Please try again.');
    } finally {
      this.setLoadingState(false);
    }
  }

  buildPageUrl(page) {
    const url = new URL(window.location.href);
    // Preserve existing query parameters (like sort_by, filters, etc.)
    if (page === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page);
    }
    return url.toString();
  }

  updateUrl(page) {
    const url = this.buildPageUrl(page);
    const state = { page: page };
    
    history.pushState(state, '', url);
    
    // Update canonical tag
    this.updateCanonicalTag(page);
    
    // Dispatch event for analytics or other listeners
    window.dispatchEvent(new CustomEvent('collection:pagechange', {
      detail: { page, url }
    }));
  }

  updateCanonicalTag(page) {
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }

    canonicalTag.setAttribute('href', `${window.location.origin}${window.location.pathname}`);
  }

  updateProductCount() {
    if (!this.elements.productCount) return;
    // Ensure we're using the correct calculated value
    const productsShown = Math.min(
      this.state.currentPage * parseInt(this.container.dataset.productsPerPage || 20),
      this.state.totalProducts
    );
    this.elements.productCount.textContent = productsShown;
    // Also update state to keep it in sync
    this.state.productsLoaded = productsShown;
  }

  updateProgressBar() {
    if (!this.elements.progressFill || !this.elements.progressText) return;
    
    // Use the correct products shown value
    const productsShown = Math.min(
      this.state.currentPage * parseInt(this.container.dataset.productsPerPage || 20),
      this.state.totalProducts
    );
    
    const percentage = Math.min(
      Math.round((productsShown / this.state.totalProducts) * 100),
      100
    );
    
    this.elements.progressFill.style.width = `${percentage}%`;
    this.elements.progressText.textContent = `${percentage}% viewed`;
  }

  updateLoadMoreButton(doc) {
    const newPagination = doc.querySelector('[data-pagination-container]');
    const newLoadMoreBtn = newPagination?.querySelector('[data-load-more-btn]');
    const currentLoadMoreBtn = this.container?.querySelector('[data-load-more-btn]');

    if (newLoadMoreBtn && currentLoadMoreBtn) {
      // Update button with new URL
      currentLoadMoreBtn.dataset.nextUrl = newLoadMoreBtn.dataset.nextUrl;
      
      // Update button text
      const newText = newPagination.querySelector('[data-load-more-text]');
      const currentText = this.container.querySelector('[data-load-more-text]');
      if (newText && currentText) {
        currentText.textContent = newText.textContent;
      }
      
      // Update element reference
      this.elements.loadMoreBtn = currentLoadMoreBtn;
      this.elements.loadMoreText = currentText;
    } else if (!newLoadMoreBtn) {
      // No more products, hide Load More button
      this.hideLoadMoreButton();
    }
  }

  hideLoadMoreButton() {
    const loadMoreContainer = this.elements.loadMoreBtn?.closest('.collection-pagination__load-more');
    if (loadMoreContainer) {
      loadMoreContainer.style.display = 'none';
    }
  }

  updatePaginationNav(doc) {
    if (!this.container) return;

    const newPagination = doc.querySelector('[data-pagination-container]');
    if (newPagination) {
      const newNav = newPagination.querySelector('[data-pagination-nav]');
      const currentNav = this.container.querySelector('[data-pagination-nav]');
      
      if (newNav && currentNav) {
        currentNav.innerHTML = newNav.innerHTML;
      } else if (newNav && !currentNav) {
        // Nav was added, append it to container
        this.container.appendChild(newNav.cloneNode(true));
      }
      
      // Update element reference if nav was recreated
      this.elements.paginationNav = this.container.querySelector('[data-pagination-nav]');
    }
  }

  setLoadingState(isLoading) {
    this.state.isLoading = isLoading;
    
    if (this.elements.loadMoreBtn) {
      this.elements.loadMoreBtn.classList.toggle('is-loading', isLoading);
      this.elements.loadMoreBtn.disabled = isLoading;
    }
  }

  reinitializeVATToggle() {
    if (typeof jQuery === 'undefined' || !jQuery.fn || !jQuery('#price-toggle').length) {
      return;
    }
    
    var session_key = 'exclVATv3';
    var session_val = sessionStorage.getItem(session_key);
    
    if (session_val == null) {
      session_val = true; // Default to exc. VAT for new users
      sessionStorage.setItem(session_key, session_val);
    } else {
      session_val = (session_val == 'true');
    }
    
    jQuery('[data-price-wrapper]').each(function(i, p) {
      if (session_val) {
        // Show without VAT Price
        jQuery(p).find('.in-vat').addClass('hidden');
        jQuery(p).find('.ex-vat').removeClass('hidden');
      } else {
        // Show VAT Price
        jQuery(p).find('.ex-vat').addClass('hidden');
        jQuery(p).find('.in-vat').removeClass('hidden');
      }
    });
  }

  showError(message) {
    // Remove existing error if any
    const existingError = document.querySelector('.collection-pagination__error');
    if (existingError) {
      existingError.remove();
    }
    
    // Create error notification
    const notification = document.createElement('div');
    notification.className = 'collection-pagination__error';
    notification.setAttribute('role', 'alert');
    notification.innerHTML = `
      <span>${message}</span>
      <button type="button" aria-label="Dismiss">×</button>
    `;
    
    // Add click handler for dismiss button
    const dismissBtn = notification.querySelector('button');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        notification.remove();
      });
    }
    
    document.body.appendChild(notification);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.remove();
    }, 5000);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionPagination();
  });
} else {
  new CollectionPagination();
}

// Re-initialize on Shopify section load (for Theme Editor)
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('[data-pagination-container]')) {
    new CollectionPagination();
  }
});
